import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeftIcon,
  PlusIcon,
  MinusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CubeIcon,
} from '@heroicons/react/24/outline'
import { inventoryService } from '../../services/inventoryService'
import { productService } from '../../services/productService'
import toast from 'react-hot-toast'

interface AdjustmentItem {
  product_id: number
  product_name: string
  sku: string
  current_stock: number
  adjustment: number
  reason: string
  notes: string
}

export const InventoryAdjustmentPage = () => {
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [selectedItems, setSelectedItems] = useState<AdjustmentItem[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const queryClient = useQueryClient()

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products', 'adjustment', search],
    queryFn: () => productService.getAll({ search, limit: 100 }),
  })

  const adjustMutation = useMutation({
    mutationFn: (adjustments: any[]) => inventoryService.adjustStock(adjustments),
    onSuccess: () => {
      toast.success('Inventory adjusted successfully')
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setSelectedItems([])
      setShowConfirmModal(false)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to adjust inventory')
    },
  })

  const products = productsData?.data?.data?.items || []

  const preselectProductId = searchParams.get('product_id')
  const { data: preselectProductData } = useQuery({
    queryKey: ['product', preselectProductId],
    queryFn: () => productService.getById(preselectProductId!),
    enabled: Boolean(preselectProductId),
  })

  useEffect(() => {
    const product = preselectProductData?.data?.data
    if (product) addToAdjustment(product)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectProductData])

  const addToAdjustment = (product: any) => {
    if (selectedItems.find(item => item.product_id === product.product_id)) {
      toast.error('Product already added')
      return
    }

    setSelectedItems([...selectedItems, {
      product_id: product.product_id,
      product_name: product.product_name,
      sku: product.sku,
      current_stock: product._count?.serial_numbers || 0,
      adjustment: 0,
      reason: 'correction',
      notes: '',
    }])
  }

  const updateAdjustment = (productId: number, field: keyof AdjustmentItem, value: any) => {
    setSelectedItems(items =>
      items.map(item =>
        item.product_id === productId ? { ...item, [field]: value } : item
      )
    )
  }

  const removeItem = (productId: number) => {
    setSelectedItems(items => items.filter(item => item.product_id !== productId))
  }

  const handleSubmit = () => {
    const validItems = selectedItems.filter(item => item.adjustment !== 0)
    if (validItems.length === 0) {
      toast.error('Please add adjustments')
      return
    }
    setShowConfirmModal(true)
  }

  const confirmAdjustment = () => {
    const adjustments = selectedItems
      .filter(item => item.adjustment !== 0)
      .map(item => ({
        product_id: item.product_id,
        quantity: item.adjustment,
        reason: item.reason,
        notes: item.notes,
      }))

    adjustMutation.mutate(adjustments)
  }

  const totalAdjustment = selectedItems.reduce((sum, item) => sum + item.adjustment, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/inventory" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stock Adjustment</h1>
            <p className="text-sm text-gray-500">Adjust inventory levels with proper audit trail</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {selectedItems.length > 0 && (
            <button
              onClick={handleSubmit}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <CheckCircleIcon className="w-4 h-4" />
              Review ({selectedItems.length})
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product Selection */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Select Products</h2>

          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : products.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CubeIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No products found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {products.map((product: any) => (
                  <button
                    key={product.product_id}
                    onClick={() => addToAdjustment(product)}
                    disabled={selectedItems.find(item => item.product_id === product.product_id) !== undefined}
                    className="w-full p-3 flex items-center justify-between hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{product.product_name}</p>
                      <p className="text-xs text-gray-500">{product.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{product._count?.serial_numbers || 0} in stock</p>
                      <p className="text-xs text-gray-400">Click to add</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Adjustment List */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Adjustments</h2>
            {selectedItems.length > 0 && (
              <button
                onClick={() => setSelectedItems([])}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Clear all
              </button>
            )}
          </div>

          {selectedItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Select products to adjust</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedItems.map((item) => (
                <div key={item.product_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.product_name}</p>
                      <p className="text-xs text-gray-500">{item.sku}</p>
                      <p className="text-sm text-gray-600 mt-1">Current: {item.current_stock}</p>
                    </div>
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <MinusIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    {/* Adjustment Input */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Adjustment (+/-)</label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateAdjustment(item.product_id, 'adjustment', item.adjustment - 1)}
                          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          value={item.adjustment}
                          onChange={(e) => updateAdjustment(item.product_id, 'adjustment', parseInt(e.target.value) || 0)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-center"
                        />
                        <button
                          onClick={() => updateAdjustment(item.product_id, 'adjustment', item.adjustment + 1)}
                          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        New stock level: <span className="font-medium">{item.current_stock + item.adjustment}</span>
                      </p>
                    </div>

                    {/* Reason */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
                      <select
                        value={item.reason}
                        onChange={(e) => updateAdjustment(item.product_id, 'reason', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="correction">Stock Correction</option>
                        <option value="damaged">Damaged Goods</option>
                        <option value="lost">Lost/Missing</option>
                        <option value="found">Found/Recovered</option>
                        <option value="returned">Customer Return</option>
                        <option value="consignment">Consignment</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Notes (optional)</label>
                      <input
                        type="text"
                        value={item.notes}
                        onChange={(e) => updateAdjustment(item.product_id, 'notes', e.target.value)}
                        placeholder="Add notes..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Adjustment:</span>
                  <span className={`text-xl font-bold ${totalAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalAdjustment >= 0 ? '+' : ''}{totalAdjustment}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Stock Adjustment</h3>
            <p className="text-gray-600 mb-4">
              You are about to adjust {selectedItems.filter(i => i.adjustment !== 0).length} product(s).
              This action will be logged for audit purposes.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto">
              {selectedItems.filter(i => i.adjustment !== 0).map(item => (
                <div key={item.product_id} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm">{item.product_name}</span>
                  <span className={`text-sm font-medium ${item.adjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {item.adjustment >= 0 ? '+' : ''}{item.adjustment}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAdjustment}
                disabled={adjustMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {adjustMutation.isPending ? 'Processing...' : 'Confirm Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
